import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Card, CardContent, CardActions, Grid, Divider, Switch,
    Chip, useTheme, Paper, Avatar, Link, Rating, Container, CssBaseline
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import LaunchIcon from '@mui/icons-material/Launch';
import StarIcon from '@mui/icons-material/Star';
import { motion } from 'framer-motion';
import { loadStripe } from '@stripe/stripe-js';
import { TopBar } from '../../components/top_bar/TopBar';
import axios from 'axios';
import { useUser } from '@clerk/clerk-react';
import { GetUser } from '../../components/auth/GetUser';
import { Helmet } from 'react-helmet';
import './Pricing.css';
import posthog from 'posthog-js';

// planName: 'Basic', 'Pro', 'Expert'
// plan: 'Basic',  'monthlyPro', ....

const PricingCard = ({ planName, price, priceSubText, smallfont, features, buttonText, onSubscribe,
    isCurrentPlan,
    isPremium,
    premiumUntil, onCancel, onRenew, subscriptionStatus,
}) => {
    const cardClassName =
        planName === 'Basic'
            ? 'pricingCard'
            : isCurrentPlan
                ? 'pricingCard-current'
                : 'pricingCard-premium';
    return (
        <Card
            className={cardClassName} sx={{ position: 'relative', overflow: 'visible' }}
        >
            {planName === 'Pro' && (
                <Box
                    sx={{
                        position: 'absolute',
                        width: '60%',
                        top: 0,
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'green',
                        color: 'white',
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        padding: '5px 8px',
                        borderRadius: '6px',
                        zIndex: 10,
                    }}
                >
                    Most Popular
                </Box>
            )}
            <CardContent sx={{ height: '450px' }}>
                <Typography className='title-text-huge' gutterBottom>
                    {planName}
                </Typography>
                <Typography className='normal-text-medium-grey-bold'>
                    {priceSubText}
                </Typography>
                <Typography className='title-text-light-huge' style={{ marginTop: '5px' }}>
                    {price}
                </Typography>
                {smallfont ? (
                    <Typography className="title-text-mini">{smallfont}</Typography>
                ) : (
                    <Typography className="title-text-mini">&nbsp;</Typography>
                )}
                <Divider sx={{ marginY: 2 }} />
                <ul style={{ listStyleType: 'none', paddingLeft: 0, textAlign: 'left' }}>
                    {features.map((feature, index) => (
                        <li key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                            <DoneIcon sx={{ color: 'blue', fontSize: 'small', marginRight: '8px' }} />
                            <Typography className={index === 0 ? 'title-text-mini-bold' : 'title-text-mini'}>
                                {feature}
                            </Typography>
                        </li>
                    ))}
                </ul>
            </CardContent>
            <CardActions
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',  // or center if you like
                    gap: 1                     // space between items
                  }}
            >
                {isCurrentPlan && isPremium ? (
                    <>
                        <Typography className='mini-secondary-alert' >
                            Membership will end on {new Date(premiumUntil).toLocaleDateString()}
                        </Typography>
                        {subscriptionStatus === 'active' ? (
                            <Button className='main-button-huge' onClick={onCancel}>Cancel Subscription</Button>
                        ) : subscriptionStatus === 'cancel_at_period_end' && new Date() < new Date(premiumUntil) ? (
                            <Button className='main-button-huge' onClick={onRenew}>Renew Now</Button>
                        ) : (
                            <Button className='main-button-huge' onClick={onSubscribe}>
                                {buttonText}
                            </Button>
                        )}
                    </>
                ) : (
                    <Button className='main-button-huge' onClick={onSubscribe}>
                        {buttonText}
                    </Button>
                )}
            </CardActions>

        </Card>
    );
};

const App = () => {
    const [curuser, setCuruser] = useState(null);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [paymentCanceled, setPaymentCanceled] = useState(false);
    const TITLE = 'Pricing - Mirwork';

    const { isSignedIn, user, isLoaded } = useUser();
    const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

    const [annual, setAnnual] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    const proMonthlyPrice = 29.99;
    const proAnnualPrice = 8.33;
    const proAnnualCharge = 99.99;
    const proSavings = Math.round(
        ((proMonthlyPrice * 12 - proAnnualCharge) / (proMonthlyPrice * 12)) * 100
    );
    const expertMonthlyPrice = 149.99;
    const expertAnnualPrice = 99.99;
    const expertAnnualCharge = 1199.88;

    const expertSavings = Math.round(
        ((expertMonthlyPrice * 12 - expertAnnualCharge) / (expertMonthlyPrice * 12)) * 100
    );

    const maxSavings = Math.max(proSavings, expertSavings);
    const theme = useTheme();

    const handleSubscribe = async (plan) => {
        console.log(plan)
        if (plan === 'Basic') {
            posthog.capture('payment_free_plan_clicked', {
                plan_name: plan,
                userID: curuser.id
            })
            window.location.href = `${process.env.REACT_APP_FRONTEND_URL}/interview`;
            return;
        }

        posthog.capture('payment_plan_clicked', {
            plan_name: plan,
            userID: curuser.id
        })

        if (plan === "MonthlyPro" || plan === "MonthlyExpert") {
            posthog.capture('payment_month_plan_clicked', {
                plan_name: plan,
                userID: curuser.id
            })
        } else {
            posthog.capture('payment_year_plan_clicked', {
                plan_name: plan,
                userID: curuser.id
            })
        }

        const stripe = await stripePromise;

        try {
            const apiUrl = process.env.REACT_APP_BACKEND_URL + "/api/userData/create-checkout-session";
            const response = await axios.post(apiUrl, {
                userID: curuser.id,
                plan: plan,
            });
            const session = response.data;

            const result = await stripe.redirectToCheckout({
                sessionId: session.id,
            });

            if (result.error) {
                console.error(result.error.message);
            }
        } catch (error) {
            console.error("Error creating checkout session:", error);
        }
    };


    const handleCancel = async () => {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/userData/cancel-subscription`, { userID: curuser.id });
        setCuruser(u => ({ ...u, subscriptionStatus: 'cancel_at_period_end' }));
    };

    const handleRenew = async (plan) => {
        const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/userData/create-checkout-session`, {
            userID: curuser.id,
            plan,
        });
        const stripe = await stripePromise;
        await stripe.redirectToCheckout({ sessionId: response.data.id });
    };


    useEffect(
        () => {
            const fetchUserData = async () => {
                if (isLoaded) {
                    console.log(user)
                    let curUser = await GetUser(isSignedIn, user);
                    setCuruser(curUser)
                    console.log(curUser)
                    posthog.identify(user.primaryEmailAddress.emailAddress)
                }
            };
            fetchUserData();
        }, [isSignedIn, user, isLoaded]);

    useEffect(() => {
        posthog.capture('$pageview', {
            $current_url: window.location.href,
            $host: window.location.hostname,
            $pathname: window.location.pathname,
        });
        const queryParams = new URLSearchParams(window.location.search);
        if (queryParams.get('success')) {
            setPaymentSuccess(true);
        }
        if (queryParams.get('canceled')) {
            setPaymentCanceled(true);
        }
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 2) % chromeStoreReviews.length);
        }, 3000);

        return () => clearInterval(interval);
    }, []);

    const fadeVariants = {
        hidden: { opacity: 1, x: 20 },
        enter: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: -20 }
    };

    const containerAnimation = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const pricingPlans = [
        {
            planName: 'Basic',
            price: 'Free',
            priceSubText: 'Best for students who want a quick practice session',
            smallfont: '',
            features: [
                '3 AI practice interviews/month',
                '15-min interview sessions',
                'Basic interviewer role',
                'Basic interview question bank',
            ],
            buttonText: 'Start Free',
            onSubscribe: () => handleSubscribe('Basic'),
            onRenew: () => handleSubscribe('Basic'),
        },
        {
            planName: 'Pro',
            price: annual ? '$8.33/month' : '$29.99/month',
            priceSubText: 'Best for serious job seekers who want to improve their interview performance',
            smallfont: annual ? `Charged $${proAnnualCharge} annually` : null,
            features: [
                'Unlimited AI practice Interviews',
                '45-min interview sessions',
                'Advanced interviewer roles',
                'Expert-vetted interview question bank',
                'Personalized feedback summary',
                "Detailed improvement feedback",
            ],
            buttonText: 'Get started',
            onSubscribe: () => handleSubscribe(annual ? 'AnnualPro' : 'MonthlyPro'),
            onRenew: () => handleRenew(annual ? 'AnnualPro' : 'MonthlyPro')
        },
        {
            planName: 'Expert',
            price: annual ? '$99.99/month' : '$149.99/month',
            priceSubText: 'For career advancement',
            smallfont: annual ? `Charged $${expertAnnualCharge} annually` : null,
            features: [
                'Unlimited AI Mock Interviews',
                'Advanced Analytics Dashboard',
                'Industry-Specific Questions',
                'Flexible Career Coaching (45 min total)',
                'Interview Strategy Guide',
                "CEO's phone number to ask for advice",
                'Custom Interview Templates',
            ],
            buttonText: 'Get started',
            onSubscribe: () => handleSubscribe(annual ? 'AnnualExpert' : 'MonthlyExpert'),
            onRenew: () => handleRenew(annual ? 'AnnualExpert' : 'MonthlyExpert')
        },
    ];

    const chromeStoreReviews = [
        {
            avatar:
                'https://lh3.googleusercontent.com/a-/ALV-UjUc0214aZeojcQKAvE2l-zITFo9FcQ88X0_-VF4w0WAQ84X3l6l=s96-w96-h96',
            rating: 5,
            comment:
                'Absolutely amazing. This is the BEST interview practising tool I have experienced, miles away from its competitors. Very very well done.',
            name: 'Julien USA'
        },
        {
            avatar:
                'https://lh3.googleusercontent.com/a-/ALV-UjXaVE9llh89oiOnKZ5v2CDMpHyBAjN-6Gf-NzmMtlkdLjPjR-dy=s96-w96-h96',
            rating: 5,
            comment:
                'A very visionary and useful tool! All job seekers should have this!',
            name: 'Bryan Lai',
        },
        {
            avatar:
                'https://lh3.googleusercontent.com/a/ACg8ocK5baLfVrNX2LO_TdZ2NO2V17BfwPWA8q015nmZ2hr6FTia6A=s96-w96-h96',
            rating: 5,
            comment:
                'This is a very interesting tool to help with mock interviews. The mock interview platform was able to follow up with the interviewee\'s answer and based on that asked new questions. Response time was fast, creating the feeling that I was in an actual interview. There was also a very detailed scorecard at the end of along with interview transcript, helping the interviewee to better understand their performance.',
            name: 'Ashley Do',
        },
        {
            avatar:
                'https://lh3.googleusercontent.com/a/ACg8ocLFMxMYYpcXy9g_cr1Ndh2q9ksnaURCxYxUb25_35td49gRNQ=s96-w96-h96',
            rating: 5,
            comment:
                'I was skeptical at first, but Quick Mock turned out to be a fantastic tool. I used it to prepare for a product management role, and the experience was seamless. I’d recommend this to anyone preparing for interviews. It’s like having a personal career coach at your fingertips!',
            name: 'Bo Ni',
        },
        {
            avatar:
                'https://lh3.googleusercontent.com/a-/ALV-UjUJXJAhnrJ-t5IHcp-oY_NR6o3vLgGrUhLEyI0VcwsvAI2yb3o=s96-w96-h96',
            rating: 5,
            comment:
                'So cool! It really helped me feel more confident going into interviews and gave me a heads-up on some key questions I might face.',
            name: 'Yafei Fan',
        },
        {
            avatar:
                'https://lh3.googleusercontent.com/a-/ALV-UjUOUChzkrQsAMwOLsp0rws3fwh93MvXhLUc5tchochBOUMeeFpY=s96-w96-h96',
            rating: 5,
            comment:
                'Wow! I used Quick Mock to prepare for a marketing role I found on LinkedIn, and it was amazing. Clicking the Quick Mock button instantly started a tailored mock interview based on the job description. The questions were relevant, and the feedback was super helpful—I learned how to make my answers more concise and impactful. It felt like practicing for the real thing. If you’re job hunting, give this a try—you won’t regret it!',
            name: 'Qianqian Yang',
        },
        {
            avatar:
                'https://lh3.googleusercontent.com/a/ACg8ocJpPAU8di-Hui-u8mzk8G9lciU4cNsaWwniAxgCAQIhN0RWug=s96-w96-h96',
            rating: 5,
            comment:
                'It\'s lowkey kinda cool! The questions generated were pretty spot-on and not generic at all.',
            name: 'Aria',
        },
    ];

    const firstReview = chromeStoreReviews[currentIndex];
    const secondReview =
        chromeStoreReviews[(currentIndex + 1) % chromeStoreReviews.length];

    const displayedReviews = [firstReview, secondReview];

    return (
        <>
            <Helmet>
                <title>{TITLE}</title>
            </Helmet>
            <Box>
                <Box padding={4}>
                    <Typography
                        variant="h3"
                        component="h1"
                        sx={{
                            textAlign: 'center',
                            fontWeight: 'bold',
                            mb: 2,
                            background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            marginTop: '20px',
                            marginBottom: '20px'
                        }}
                    >
                        Invest in Your Career Success
                    </Typography>
                    <Typography variant="h6" color="text.secondary" style={{ textAlign: 'center', marginBottom: '30px', marginTop: '20px' }}>
                        Choose the perfect plan to enhance your interview skills and land your dream job
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 2
                        }}
                    >
                        <Typography
                            variant="body2"
                            color={!annual ? theme.palette.primary.main : 'text.secondary'}
                        >
                            Monthly
                        </Typography>

                        <Switch
                            checked={annual}
                            onChange={() => setAnnual(!annual)}
                            color="primary"
                        />

                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography
                                variant="body2"
                                color={annual ? theme.palette.primary.main : 'text.secondary'}
                            >
                                Annual
                            </Typography>
                            <Chip
                                label={`Save up to ${maxSavings}%`}
                                size="large"
                                sx={{
                                    ml: 1,
                                    fontWeight: 'bold',
                                    color: '#fff', // optional: ensures good contrast
                                    backgroundColor: theme.palette.primary.main,
                                    animation: 'colorPulse 2s infinite',
                                    '@keyframes colorPulse': {
                                        '0%': {
                                            backgroundColor: theme.palette.primary.main,
                                        },
                                        '50%': {
                                            backgroundColor: theme.palette.secondary.main,
                                        },
                                        '100%': {
                                            backgroundColor: theme.palette.primary.main,
                                        },
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                    <Box sx={{ width: '100%', margin: '0 auto', marginTop: '50px' }}>
                        <Grid container spacing={10} justifyContent="center">
                            {pricingPlans.map((plan, index) => (
                                <Grid item key={index}>
                                    <PricingCard
                                        {...plan}
                                        isCurrentPlan={(curuser?.subscriptionLevel || 'Basic') === plan.planName}
                                        isPremium={curuser?.premium}
                                        premiumUntil={curuser?.premiumUntil}
                                        subscriptionStatus={curuser?.subscriptionStatus}
                                        onCancel={handleCancel}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                    <Box sx={{ alignItems: 'center', justifyContent: 'center', }}>
                        {paymentSuccess && (
                            <Box
                                sx={{
                                    bgcolor: 'success.main',
                                    color: 'white',
                                    padding: 2,
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginTop: 4,
                                }}
                            >
                                <Typography className='text-white-mini-bold'>
                                    Payment Successful! You are now a Premium Member.
                                </Typography>
                            </Box>
                        )}
                        {paymentCanceled && (
                            <Box
                                sx={{
                                    bgcolor: 'error.main',
                                    color: 'white',
                                    padding: 1,
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginTop: 4,
                                }}
                            >
                                <Typography className='text-white-mini-bold'>
                                    Payment was canceled. Please try again if you wish to subscribe.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
                <Box
                    component={motion.div}
                    {...containerAnimation}
                    sx={{
                        textAlign: 'center',
                        mt: 8,
                        maxWidth: 700,
                        mx: 'auto'
                    }}
                >
                    <Typography variant="h5" component="h2" fontWeight="bold" gutterBottom>
                        100% Satisfaction Guaranteed
                    </Typography>
                    <Typography color="text.secondary">
                        Try our AI mock interviews risk-free. If you're not completely satisfied within the first
                        14 days, we'll refund your investment – no questions asked.
                    </Typography>
                </Box>
                <Box sx={{ mt: 10, maxWidth: 800, mx: 'auto' }}>
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom sx={{ fontSize: '1.8rem' }}>
                            Trusted by Job Seekers
                        </Typography>
                        <Box sx={{ mb: 1 }}>
                            <Rating
                                value={5}
                                readOnly
                                icon={<StarIcon fontSize="inherit" style={{ color: '#F59E0B' }} />}
                                emptyIcon={<StarIcon fontSize="inherit" style={{ color: '#F59E0B' }} />}
                                sx={{ mb: 1 }}
                            />
                        </Box>
                        <Link
                            href="https://chromewebstore.google.com/detail/ai-interviewer-mir-by-mir/dlanhacghegnnloapoliciamiiabbepo/reviews?hl=en-US"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: theme.palette.primary.dark,
                                '&:hover': { color: 'primary.dark' }
                            }}
                        >
                            <span>View on Chrome Web Store</span>
                            <LaunchIcon fontSize="small" />
                        </Link>
                    </Box>
                    <Box sx={{ height: '450px' }}>
                        <Grid container spacing={4}>
                            {displayedReviews.map((review, i) => (
                                <Grid item xs={12} md={6} key={`${review.name}-${i}`}>
                                    <Paper
                                        component={motion.div}
                                        variants={fadeVariants}
                                        initial="hidden"
                                        animate="enter"
                                        exit="exit"
                                        transition={{ duration: 0.5 }}
                                        elevation={2}
                                        sx={{ p: 3, borderRadius: 2 }}
                                    >
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <Avatar
                                                src={review.avatar}
                                                alt={review.name}
                                                sx={{ width: 48, height: 48, bgcolor: 'primary.light' }}
                                            />
                                            <Box>
                                                <Rating
                                                    value={review.rating}
                                                    readOnly
                                                    size="small"
                                                    sx={{ mb: 0.5 }}
                                                    icon={<StarIcon fontSize="inherit" />}
                                                    emptyIcon={<StarIcon fontSize="inherit" />}
                                                />
                                                <Typography paragraph fontSize="0.95rem" color="text.secondary">
                                                    "{review.comment}"
                                                </Typography>
                                                <Typography fontWeight="bold">{review.name}</Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Box>
            </Box>
        </>
    );
};




export default () => {
    return (
        <>
            <TopBar />
            <App />
        </>
    );
};  
